/* =============================================================================
   Shared ECharts configuration
   -----------------------------------------------------------------------------
   Every chart in the app is built from `baseChartOption()` so grid, axes,
   tooltip, zoom and mark specs are identical everywhere and only the data and
   its accent colour change.

   Colour rules encoded here:
     • Series hues come from the validated categorical palette (see
       tokens.css). Assign in fixed order, never cycle, never generate.
     • Axis, grid and label ink use text tokens — never the series colour.
     • A 2-series-or-more chart always renders its legend; series names are
       therefore never carried by colour alone.
   ========================================================================== */

/** Read a CSS custom property with a fallback, safe during SSR/tests. */
const cssVar = (name, fallback) => {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
};

export const isDarkTheme = () =>
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("data-theme") === "dark";

/** Resolved chart chrome for the active theme. */
export const chartInk = () => ({
  surface: cssVar("--wg-viz-surface", "#ffffff"),
  grid: cssVar("--wg-viz-grid", "#e9edf2"),
  axis: cssVar("--wg-viz-axis", "#cbd5e1"),
  label: cssVar("--wg-viz-label", "#7c8899"),
  ink: cssVar("--wg-ink", "#0f172a"),
  inkSecondary: cssVar("--wg-ink-secondary", "#475569"),
  tooltipBg: cssVar("--wg-viz-tooltip-bg", "#0f172a"),
  tooltipInk: cssVar("--wg-viz-tooltip-ink", "#f1f5f9"),
});

/** The validated categorical order. Index 0 first, always. */
export const SERIES_SLOTS = [
  "--wg-series-1",
  "--wg-series-2",
  "--wg-series-3",
  "--wg-series-4",
  "--wg-series-5",
  "--wg-series-6",
  "--wg-series-7",
  "--wg-series-8",
];

const SERIES_FALLBACK = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
];

export const seriesColor = (index = 0) =>
  cssVar(
    SERIES_SLOTS[index % SERIES_SLOTS.length],
    SERIES_FALLBACK[index % SERIES_FALLBACK.length]
  );

/** Per-metric identity colour, shared by icon, chip and chart line. */
export const METRIC_COLORS = {
  accelerometer: { token: "--wg-metric-accelerometer", fallback: "#2a78d6" },
  ecg: { token: "--wg-metric-ecg", fallback: "#4a3aa7" },
  heartRate: { token: "--wg-metric-heartrate", fallback: "#e34948" },
  ppg: { token: "--wg-metric-ppg", fallback: "#008300" },
  spo2: { token: "--wg-metric-spo2", fallback: "#2a78d6" },
  skinTemperature: { token: "--wg-metric-skintemp", fallback: "#eb6834" },
  eda: { token: "--wg-metric-eda", fallback: "#e87ba4" },
  bodyComposition: { token: "--wg-metric-body", fallback: "#1baf7a" },
};

export const metricColor = (key) => {
  const entry = METRIC_COLORS[key];
  if (!entry) return seriesColor(0);
  return cssVar(entry.token, entry.fallback);
};

/** rgba() from a #rrggbb string — used for the soft area fill under a line. */
export const withAlpha = (hex, alpha) => {
  const clean = String(hex || "").replace("#", "");
  if (clean.length !== 6) return hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Vertical gradient fill for an area series. Subtle by design — the line
 *  carries the signal, the fill only anchors it to the baseline. */
export const areaGradient = (color) => ({
  type: "linear",
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: withAlpha(color, 0.22) },
    { offset: 1, color: withAlpha(color, 0.0) },
  ],
});

const AXIS_LABEL_STYLE = (ink) => ({
  color: ink.label,
  fontSize: 11,
  fontWeight: 500,
  fontFamily: "inherit",
});

/**
 * The shared chart skeleton.
 *
 * @param {object}  opts
 * @param {array}   opts.categories   x-axis category values
 * @param {array}   opts.series       ECharts series definitions
 * @param {boolean} opts.showLegend   force the legend on/off (defaults to
 *                                    "on when more than one series")
 * @param {string}  opts.yName        unit suffix rendered on the y axis
 * @param {string}  opts.xName        label for the x axis
 * @param {number}  opts.yMin/opts.yMax   explicit y bounds
 * @param {boolean} opts.zoom         include the zoom controls
 * @param {function} opts.tooltipFormatter  custom tooltip renderer
 */
export const baseChartOption = ({
  categories = [],
  series = [],
  showLegend,
  yName = "",
  xName = "",
  yMin,
  yMax,
  zoom = true,
  tooltipFormatter,
  gridTop,
} = {}) => {
  const ink = chartInk();
  const withLegend = showLegend ?? series.length > 1;

  return {
    backgroundColor: "transparent",
    animationDuration: 320,
    animationEasing: "cubicOut",
    textStyle: { fontFamily: "inherit", color: ink.inkSecondary },

    color: series.map((s, i) => s.__accent || seriesColor(i)),

    grid: {
      left: 8,
      right: 16,
      top: gridTop ?? (withLegend ? 44 : 16),
      bottom: zoom ? 52 : 24,
      containLabel: true,
    },

    legend: withLegend
      ? {
          top: 4,
          left: 0,
          itemWidth: 12,
          itemHeight: 3,
          itemGap: 18,
          icon: "roundRect",
          textStyle: {
            color: ink.inkSecondary,
            fontSize: 12,
            fontWeight: 500,
            fontFamily: "inherit",
          },
          inactiveColor: ink.label,
        }
      : { show: false },

    tooltip: {
      trigger: "axis",
      confine: true,
      backgroundColor: ink.tooltipBg,
      borderWidth: 0,
      padding: [8, 12],
      extraCssText: "border-radius:8px; box-shadow:0 12px 28px -14px rgba(0,0,0,.4);",
      textStyle: {
        color: ink.tooltipInk,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "inherit",
      },
      axisPointer: {
        type: "line",
        lineStyle: { color: ink.axis, width: 1, type: [4, 4] },
        label: { show: false },
      },
      ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
    },

    xAxis: {
      type: "category",
      data: categories,
      boundaryGap: false,
      name: xName,
      nameLocation: "middle",
      nameGap: 28,
      nameTextStyle: { color: ink.label, fontSize: 11 },
      axisLine: { lineStyle: { color: ink.axis } },
      axisTick: { show: false },
      axisLabel: { ...AXIS_LABEL_STYLE(ink), hideOverlap: true },
      splitLine: { show: false },
    },

    yAxis: {
      type: "value",
      min: yMin,
      max: yMax,
      scale: yMin === undefined && yMax === undefined,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...AXIS_LABEL_STYLE(ink),
        formatter: yName ? `{value} ${yName}` : "{value}",
      },
      splitLine: { show: true, lineStyle: { color: ink.grid, width: 1 } },
    },

    dataZoom: zoom
      ? [
          { type: "inside", start: 0, end: 100, zoomOnMouseWheel: true },
          {
            type: "slider",
            height: 20,
            bottom: 8,
            borderColor: "transparent",
            backgroundColor: "transparent",
            fillerColor: withAlpha(seriesColor(0), 0.1),
            handleStyle: { color: ink.axis, borderColor: ink.axis },
            moveHandleStyle: { color: ink.axis },
            dataBackground: {
              lineStyle: { color: ink.axis, width: 1 },
              areaStyle: { color: "transparent" },
            },
            selectedDataBackground: {
              lineStyle: { color: ink.label },
              areaStyle: { color: "transparent" },
            },
            textStyle: { color: ink.label, fontSize: 10 },
            brushSelect: false,
          },
        ]
      : undefined,

    series,
  };
};

/**
 * A line/area series with the house mark spec: 2px stroke, markers hidden
 * until hover (so dense traces stay readable), a 2px surface ring on the
 * emphasised point.
 *
 * @param {string} lineType  "solid" | "dashed" | "dotted" — the secondary
 *   encoding that lets a series be told apart without relying on hue.
 */
export const lineSeries = ({
  name,
  data,
  color,
  area = true,
  smooth = 0.2,
  lineType = "solid",
  width = 2,
  showSymbol = false,
}) => {
  const ink = chartInk();
  return {
    name,
    type: "line",
    data,
    smooth,
    showSymbol,
    symbol: "circle",
    symbolSize: 8,
    sampling: "lttb",
    __accent: color,
    lineStyle: { color, width, type: lineType, cap: "round", join: "round" },
    itemStyle: { color, borderColor: ink.surface, borderWidth: 2 },
    emphasis: {
      focus: "series",
      scale: 1.15,
      itemStyle: { color, borderColor: ink.surface, borderWidth: 2 },
    },
    ...(area ? { areaStyle: { color: areaGradient(color), origin: "start" } } : {}),
  };
};

export default baseChartOption;
